import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Box, Alert, CircularProgress, Paper, Badge } from '@mui/material'
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { dashboardService } from '../../services/dashboardService'
import { UserService } from '../../services/userService'
import { QuestionReportService } from '../../services/questionReportService'
import {
  RecentActivity,
  RiskOverview,
  DashboardStats,
  MonthlyTrendChart,
  MonthlyTrendData,
  AnnouncementCard,
} from '../../components/dashboard'
import { TutorialModal, useAdminOnboardingTutorialSteps } from '../../components/tutorial'
import { Activity } from '../../types/dashboard'

export const AdminDashboard: React.FC = () => {
  const { t } = useTranslation('dashboard')
  const { t: tTutorial } = useTranslation('tutorial')
  const { t: tReport } = useTranslation('questionReport')
  const navigate = useNavigate()
  const { user } = useAuth()
  const [riskStats, setRiskStats] = useState<DashboardStats | null>(null)
  const [activities, setActivities] = useState<Activity[]>([])
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyTrendData[]>([])
  const [reportCount, setReportCount] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // チュートリアル関連の状態
  const [showTutorial, setShowTutorial] = useState(false)
  const [tutorialChecked, setTutorialChecked] = useState(false)
  const tutorialSteps = useAdminOnboardingTutorialSteps()

  useEffect(() => {
    let isMounted = true

    const loadDashboardData = async () => {
      if (!user) return

      try {
        setLoading(true)
        setError('')

        const [statsData, activityData, trendData, reportCountData] = await Promise.all([
          dashboardService.getRiskStats(),
          dashboardService.getRecentActivity(5),
          dashboardService.getMonthlyTrend(),
          QuestionReportService.getReportCount().catch(() => ({ count: 0 })),
        ])

        if (isMounted) {
          setRiskStats(statsData)
          setActivities(activityData)
          setMonthlyTrend(trendData)
          setReportCount(reportCountData.count)
        }
      } catch (err) {
        console.error('Dashboard data loading failed:', err)
        if (isMounted) setError(t('loadError'))
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadDashboardData()

    return () => {
      isMounted = false
    }
  }, [user, t])

  // チュートリアル状況確認（CORPORATE_ADMINのみ、初回のみ）
  useEffect(() => {
    const checkTutorial = async () => {
      // CORPORATE_ADMIN のみチェック（SUPER_ADMINは対象外）
      if (!user || user.role !== 'CORPORATE_ADMIN' || tutorialChecked) {
        return
      }

      try {
        const { tutorialCompleted } = await UserService.getTutorialStatus()
        // adminOnboardingチュートリアルが未完了の場合、自動表示
        if (!tutorialCompleted?.adminOnboarding) {
          setShowTutorial(true)
        }
      } catch (err) {
        console.error('チュートリアル状況取得エラー:', err)
      } finally {
        setTutorialChecked(true)
      }
    }

    checkTutorial()
  }, [user, tutorialChecked])

  // チュートリアル完了処理
  const handleTutorialComplete = async () => {
    try {
      await UserService.completeTutorial('adminOnboarding')
      setShowTutorial(false)
    } catch (err) {
      console.error('チュートリアル完了マークエラー:', err)
      setShowTutorial(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box>
      {/* 管理者オンボーディングチュートリアル（初回ログイン時に表示） */}
      <TutorialModal
        open={showTutorial}
        onClose={() => setShowTutorial(false)}
        onComplete={handleTutorialComplete}
        title={tTutorial('adminOnboarding.title')}
        steps={tutorialSteps}
      />

      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {t('adminTitle')}
      </Typography>

      {/* 2カラムレイアウト: メインコンテンツ左、お知らせ右 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: 'flex-start',
          gap: 3,
        }}
      >
        {/* メインコンテンツ */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {riskStats && <RiskOverview stats={riskStats} />}

          {/* 設問報告カード */}
          {reportCount > 0 && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' },
              }}
              onClick={() => navigate('/tests?tab=reports')}
            >
              <Badge badgeContent={reportCount} color="primary">
                <HelpOutlineIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
              </Badge>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                  {tReport('dashboard.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tReport('dashboard.count', { count: reportCount })}
                </Typography>
              </Box>
            </Paper>
          )}

          {monthlyTrend.length > 0 && <MonthlyTrendChart data={monthlyTrend} />}

          <RecentActivity activities={activities} />
        </Box>

        {/* 右サイドバー: お知らせ */}
        <Box
          sx={{
            width: { xs: '100%', lg: 280 },
            flexShrink: 0,
          }}
        >
          <AnnouncementCard
            userRole={user?.role as 'CORPORATE_ADMIN' | 'USER' | 'SUPER_ADMIN'}
            variant="sidebar"
          />
        </Box>
      </Box>
    </Box>
  )
}
